/**
 * Registro de "trabalho em andamento" (formulário não salvo, upload, gravação).
 *
 * Usado para nunca recarregar o app no meio de algo — nem nesta aba, nem em
 * outra aba do AutoFluxo no domínio principal.
 *
 * Limitação assumida: `BroadcastChannel` não garante entrega nem avisa
 * quando uma aba morre. Por isso cada aba envia um sinal periódico
 * (heartbeat). Sinal atrasado NÃO significa "aba liberada": significa
 * "não sei", e nesse caso a atualização é adiada com aviso ao usuário.
 */

const CHANNEL_NAME = 'autofluxo-busy';
const HEARTBEAT_MS = 5_000;
/** Depois disso o sinal está velho: a aba pode estar suspensa (não é segura). */
const STALE_MS = 15_000;
/** Depois disso consideramos a aba realmente encerrada. */
const DEAD_MS = 90_000;

export type BusyState = 'free' | 'busy' | 'unknown';

interface RemoteTab {
  count: number;
  lastSeen: number;
}

const busyKeys = new Set<string>();
const listeners = new Set<(state: BusyState) => void>();
const remoteTabs = new Map<string, RemoteTab>();

const TAB_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

let channel: BroadcastChannel | null = null;
let heartbeatTimer: number | null = null;

function post(payload: Record<string, unknown>) {
  try {
    channel?.postMessage({ tab: TAB_ID, ...payload });
  } catch {
    /* ignore */
  }
}

function broadcastState() {
  post({ type: 'state', count: busyKeys.size, at: Date.now() });
}

function notify() {
  const state = getAppBusyState();
  listeners.forEach((cb) => cb(state));
}

function pruneDeadTabs() {
  const now = Date.now();
  let changed = false;
  for (const [tab, info] of remoteTabs) {
    if (now - info.lastSeen > DEAD_MS) {
      remoteTabs.delete(tab);
      changed = true;
    }
  }
  return changed;
}

if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  channel = new BroadcastChannel(CHANNEL_NAME);

  channel.onmessage = (event) => {
    const data = event.data as
      | { tab?: string; type?: string; count?: number }
      | null;
    if (!data?.tab || data.tab === TAB_ID) return;

    if (data.type === 'ask') {
      broadcastState();
      return;
    }
    if (data.type === 'bye') {
      remoteTabs.delete(data.tab);
      notify();
      return;
    }
    remoteTabs.set(data.tab, { count: data.count ?? 0, lastSeen: Date.now() });
    notify();
  };

  post({ type: 'ask' });
  broadcastState();

  heartbeatTimer = window.setInterval(() => {
    broadcastState();
    if (pruneDeadTabs()) notify();
  }, HEARTBEAT_MS);

  const sayGoodbye = () => {
    post({ type: 'bye' });
    if (heartbeatTimer) window.clearInterval(heartbeatTimer);
  };
  window.addEventListener('pagehide', sayGoodbye);
  window.addEventListener('beforeunload', sayGoodbye);

  // Ao voltar do segundo plano, o sinal local pode estar velho: reanuncia
  // e pede o estado das outras abas novamente.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      post({ type: 'ask' });
      broadcastState();
    }
  });
}

export function markBusy(key: string) {
  busyKeys.add(key);
  broadcastState();
  notify();
}

export function clearBusy(key: string) {
  busyKeys.delete(key);
  broadcastState();
  notify();
}

/** Há trabalho em andamento nesta aba? */
export function isTabBusy(): boolean {
  return busyKeys.size > 0;
}

/**
 * Estado consolidado:
 * - 'busy': alguém (aqui ou em outra aba) tem trabalho aberto.
 * - 'unknown': existe aba cujo sinal está velho — não dá para garantir.
 * - 'free': todas as abas conhecidas responderam recentemente e estão livres.
 */
export function getAppBusyState(): BusyState {
  if (busyKeys.size > 0) return 'busy';

  const now = Date.now();
  let unknown = false;
  for (const info of remoteTabs.values()) {
    if (now - info.lastSeen > DEAD_MS) continue;
    if (info.count > 0) return 'busy';
    if (now - info.lastSeen > STALE_MS) unknown = true;
  }
  return unknown ? 'unknown' : 'free';
}

/** Compatibilidade: qualquer estado diferente de "livre" bloqueia. */
export function isAppBusy(): boolean {
  return getAppBusyState() !== 'free';
}

/**
 * Verificação final, imediatamente antes de aplicar uma atualização.
 * Trava novas operações durante a checagem para não abrir janela de corrida.
 */
let updateLocked = false;

export function isUpdateLocked(): boolean {
  return updateLocked;
}

export async function acquireUpdateWindow(): Promise<BusyState> {
  updateLocked = true;
  post({ type: 'ask' });
  // Espera curta para as outras abas responderem antes de decidir.
  await new Promise((resolve) => setTimeout(resolve, 400));
  const state = getAppBusyState();
  if (state !== 'free') updateLocked = false;
  return state;
}

export function releaseUpdateWindow() {
  updateLocked = false;
}

export function onBusyChange(cb: (state: BusyState) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
